<template>
	<ProductPage
		:parametrics="parametrics"
		:image-gallery="imageGallery"
		:product-attributes="productAttributes"
		:product-details="productDetails"
		:color-from-parametrics-in-link="colorFromParametricsInLink"
		:special-price="specialPrice"
		:product-recommendations="productRecommendations"
	/>
</template>

<script setup lang="ts">
import { queryToSelectedParametrics } from "~/utils/parametricsUrl";
import type {
	GroupedParametrics,
	ParametricListValue
} from "@nwt/norce-pwa/dist/runtime/types/api/product/product";
import { storeToRefs } from "pinia";
import { ref } from "vue";
import type {
	BlokGenericForComponents,
	BlokSEOApp,
	SpecialPrice
} from "~/types/storyblok/storyblok";
import { getLowestPriceIncVat } from "~/utils/product/getLowestPriceIncVat";
import { historicalBestPricesParams } from "~/utils/product/historicalBestPricesParams";
import { filterInactiveColorParametrics } from "~/utils/product/filterInactiveColorParametrics";
import { activeStatusID } from "~/utils/constants";
import {
	buildProductMetaDescription,
	buildProductSchemaDescription
} from "~/layers/base/utils/productMetaDescription";
import {
	buildProductMetaKeywords,
	buildProductMetaTitle
} from "~/layers/base/utils/productSeoMeta";
import { buildProductBrand } from "~/layers/base/domain/product/product-brand";
import { buildProductGtin } from "~/layers/base/domain/product/product-gtin";
import { buildOfferDates } from "~/layers/base/domain/product/product-offer-dates";

const {
	getProductByUniqueName,
	getProductParametrics,
	getFirstSecondParametricWithStock,
	selectProductVariant,
	getFirstColorParametricWithStock,
	setColorParametricsInStock,
	getSecondParametricName,
	listHistoricalBestPrices,
	getProductRelations
} = useNorcePwa();

const appStore = useAppStore();
const { fetchStory } = useStoryblokFetcher();
const config = useRuntimeConfig();
const { setPageTitle, setPageType } = usePageStore();
const route = useRoute();
const requestURL = useRequestURL();
const { getProductStock } = useNorcePwa();

const parametrics = ref<GroupedParametrics>();
const { storeContext, currentCurrency } = storeToRefs(useNorceStore());
const { fetchAllParametrics, ensureParametricsForProduct } =
	useAllParametrics();
const { locale, t } = useI18n();
const { showPageNotFound } = usePageNotFound();
const localePath = useLocalePath();
const { productDetail } = useGtmEvents();
// [NF-460] Dotdigital's product view, sent with view_item like base does.
const { productBrowse } = useDotdigital();
const { stores } = storeToRefs(useWarehousesAndStores());
const storyblokLanguage = useStoryblokLanguage();

const imageGallery = ref<BlokGenericForComponents[]>();
const productAttributes = ref<BlokGenericForComponents[]>();
const productDetails = ref<BlokGenericForComponents[]>();
const specialPrice = ref<SpecialPrice | null>(null);
const seo = ref<BlokSEOApp>();
const colorFromParametricsInLink = ref<ParametricListValue>();
const productRecommendations = ref<BlokGenericForComponents[]>();

// In onMounted, not at setup: on a client-side nav the store is only filled by
// fetchProduct() below, so a setup-time read pushed nothing and view_item was
// lost on every in-site click through to a PDP.
onMounted(() => {
	const product = useProductStore().products;

	if (product) {
		productDetail(product);
		productBrowse(product);
	}
});

const getProduct = async () => {
	const { data } = await fetchStory("layouts/product-page", {
		version: config.public.storyblokVersion,
		resolve_links: "url",
		language: storyblokLanguage.value
	});

	imageGallery.value = data.story.content.body[0].image_gallery;
	productAttributes.value = data.story.content.body[0].product_attributes;
	productDetails.value = data.story.content.body[0].product_details;
	seo.value = data.story.content.SEO;
	specialPrice.value = {
		background:
			data.story.content.body[0].special_price_background_color?.value || "",
		text: data.story.content.body[0].special_price_text_color?.value || "",
		enabled: !!data.story.content.body[0].show_special_price_end_date
	};
	productRecommendations.value =
		data.story.content.body[0].product_recommendations;
};
const fetchProduct = async () => {
	const storesIds = stores?.value?.[locale.value]?.map((store) => store.Id);
	const [productsFetched, parametricsFetched] = await Promise.all([
		getProductByUniqueName({
			uniqueName: route.params.uniqueProductName
				? (route.params.uniqueProductName as string)
				: (route.params.slug as string),
			...(storesIds?.length && { storeSeed: storesIds.toString() }),
			...storeContext.value,
			statusSeed: String(activeStatusID)
		}),
		fetchAllParametrics(),
		getProduct()
	]);

	const allParametricsForProduct = await ensureParametricsForProduct(
		productsFetched,
		parametricsFetched
	);
	const productParametrics = await getProductParametrics(
		{
			...storeContext.value
		},
		{ product: productsFetched, allParametrics: allParametricsForProduct }
	);
	if (productParametrics.color) {
		productParametrics.color = setColorParametricsInStock({
			parametrics: productParametrics,
			products: productsFetched
		});
		productParametrics.color = filterInactiveColorParametrics({
			parametrics: productParametrics,
			products: productsFetched
		});
	}

	parametrics.value = productParametrics;
	let selectedColorLocal: ParametricListValue | undefined,
		selectedSecondParametricLocal: ParametricListValue | undefined;

	const slugSelected = queryToSelectedParametrics(
		route.query as Record<string, unknown>,
		productParametrics
	);
	if (
		slugSelected.selectedColor !== undefined ||
		slugSelected.selectedSecondParametric !== undefined
	) {
		selectedColorLocal =
			slugSelected.selectedColor ??
			getFirstColorParametricWithStock({
				parametrics: productParametrics,
				products: productsFetched
			});
		colorFromParametricsInLink.value = slugSelected.selectedColor;
		const secondParam =
			productParametrics[getSecondParametricName(productParametrics)];
		if (secondParam) {
			selectedSecondParametricLocal =
				slugSelected.selectedSecondParametric ??
				getFirstSecondParametricWithStock({
					parametrics: secondParam,
					products: productsFetched,
					selectedColor: selectedColorLocal
				});
		}
	} else {
		selectedColorLocal = getFirstColorParametricWithStock({
			parametrics: productParametrics,
			products: productsFetched
		});
		const secondParamTemp =
			productParametrics[getSecondParametricName(productParametrics)];
		if (secondParamTemp) {
			selectedSecondParametricLocal = getFirstSecondParametricWithStock({
				parametrics: secondParamTemp,
				products: productsFetched,
				selectedColor: selectedColorLocal
			});
		}
	}

	useProductStore().addSelectedColor(selectedColorLocal);

	if (selectedSecondParametricLocal) {
		useProductStore().addSelectedSecondParametric(
			selectedSecondParametricLocal
		);
	}

	useProductStore().addProducts(productsFetched);
	useProductStore().addParametrics(productParametrics);

	const selectedProduct = selectProductVariant({
		selectedColorId: selectedColorLocal?.Id,
		selectedSecondParametricId: selectedSecondParametricLocal?.Id,
		products: productsFetched,
		parametricsAvailable:
			productParametrics?.color ??
			productParametrics[getSecondParametricName(productParametrics)]
	});

	// `selectProductVariant` is typed `Product` but returns `undefined` when the
	// URL asks for a color/size pair no variant has.
	if (!selectedProduct?.Id) {
		showPageNotFound();
		return;
	}

	useProductStore().addSelectedProduct(selectedProduct);

	try {
		const historicalBestPrices = await listHistoricalBestPrices(
			historicalBestPricesParams(selectedProduct.PartNo, storeContext.value)
		);
		const historicalLowestPrice = getLowestPriceIncVat(
			selectedProduct.PriceIncVat,
			historicalBestPrices
		);
		useProductStore().addLowestPrice(historicalLowestPrice);
	} catch {
		/* We're already logging error */
	}

	// set page title and type to pageStore
	setPageTitle(selectedProduct.VariantName ?? selectedProduct.Name);
	setPageType("product");
};

await fetchProduct();

const selectedProductId = computed(() => useProductStore().selectedProduct?.Id);

if (selectedProductId.value) {
	const relations = await getProductRelations({
		productId: selectedProductId.value,
		...useNorceStore().storeContext
	});

	useProductStore().addRelations(relations);
}

const seoInfo = computed(() => {
	const product = useProductStore().products;

	if (!product) {
		return {
			title: "",
			metaTitle: "",
			ogTitle: "",
			description: "",
			keywords: "",
			ogImage: ""
		};
	}

	return {
		title: `${product.VariantName || product.Name || ""}`,
		metaTitle: buildProductMetaTitle(product),
		ogTitle: product.VariantName || product.Name,
		keywords: buildProductMetaKeywords(product),
		// A product with no text in Norce still gets a description.
		description:
			buildProductMetaDescription(product) ||
			t("seo.product_description_fallback", {
				name: product.VariantName || product.Name || "",
				site: useRuntimeConfig().public.brand.name
			}),
		images: product.Files?.map((image) => image.Path) || [],
		ogImage: product.Files?.[0]?.Path,
		sku: product.PartNo,
		price: product.PriceIncVat,
		inStock: getProductStock(product) > 0
	};
});
const domain = computed(() => requestURL.origin);
if (import.meta.server) {
	// Any found product gets a card; without an image it is text-only.
	if (seoInfo.value?.title) {
		defineOgImageComponent("OgProduct", {
			image: seoInfo.value.ogImage || undefined,
			logo: appStore.siteLogo?.filename || "",
			title: seoInfo.value.ogTitle || "",
			description: seoInfo.value.description || "",
			width: 1200,
			height: 630
		});
	}
}
useSeoMeta({
	title: () => seoInfo.value.metaTitle || "",
	ogTitle: () => seoInfo.value.ogTitle || seoInfo.value.title || "",
	description: () => seoInfo.value.description || "",
	ogDescription: () => seoInfo.value.description || "",
	keywords: () => seoInfo.value.keywords || undefined,
	ogType: "product",
	ogUrl: () => `${domain.value}${route.path}`
});

// GF is a multi-brand retailer: no site-name fallback for the brand.
const productBrand = computed(() =>
	buildProductBrand(useProductStore().products?.Manufacturer?.Name)
);

useSchemaOrg(
	computed(() => [
		defineProduct({
			name: seoInfo.value.title || "",
			description: buildProductSchemaDescription(useProductStore().products),
			sku: seoInfo.value.sku || "",
			...(productBrand.value ? { brand: productBrand.value } : {}),
			// Same object as sku and price (seoInfo reads `products`), so the
			// Product node never mixes one variant's EAN with another's PartNo.
			...buildProductGtin(useProductStore().products?.EanCode),
			image: seoInfo.value.images || [],
			offers: {
				"@type": "Offer",
				url: `${domain.value}${route.path}`,
				priceCurrency: currentCurrency.value?.Code || "",
				price: seoInfo.value.price?.toString() || "",
				...buildOfferDates(useProductStore().products?.Updated),
				availability: seoInfo.value.inStock
					? "https://schema.org/InStock"
					: "https://schema.org/OutOfStock",
				itemCondition: "https://schema.org/NewCondition",
				seller: {
					"@type": "Organization",
					name: appStore.applicationNorce?.Name || ""
				}
			}
		}),
		defineBreadcrumb({
			itemListElement: [
				{
					position: 1,
					name: t("Home"),
					item: `${domain.value}${localePath("/")}`
				},
				{
					position: 2,
					name: seoInfo.value.title || "",
					item: `${domain.value}${route.path}`
				}
			]
		})
	])
);

watch(stores, () => {
	fetchProduct();
});

watch(storeContext, () => {
	fetchProduct();
});
</script>
